import { ArrowDropDown } from '@mui/icons-material';
import {
  Button,
  Drawer,
  MenuList,
  type ButtonProps as MuiButtonProps,
} from '@mui/material';
import { Tooltip, useIsMobile } from '@side/pantry';
import { useState } from 'react';
import { PopupMenu } from '@/components/PopupMenu';
import { PopupMenuItem } from '@/components/PopupMenu/PopupMenuItem';
import { GoogleIcon } from './GoogleIcon';

interface ButtonProps extends MuiButtonProps {
  dropdownIcon?: boolean;
  isMobile: boolean;
}

const StyledButton = ({
  dropdownIcon = false,
  loading = false,
  disabled = false,
  isMobile,
  ...rest
}: Partial<ButtonProps>) => {
  const buttonText = isMobile ? 'Save' : 'Save Contact';

  return (
    <Button
      {...rest}
      aria-label="Save Contact"
      disabled={disabled}
      loading={loading}
      startIcon={<GoogleIcon disabled={disabled} />}
      variant="outlined"
      color="utility"
      endIcon={disabled || !dropdownIcon ? undefined : <ArrowDropDown />}
    >
      {buttonText}
    </Button>
  );
};

interface GoogleButtonProps {
  handleSaveGoogleContact: () => void | Promise<void>;
  handleUpdateGoogleContact: () => void | Promise<void>;
  isDirty: boolean;
  loading: boolean;
  disabled: boolean;
  hasGoogleId: boolean;
  sx?: ButtonProps['sx'];
}
export function GoogleButton({
  handleSaveGoogleContact,
  handleUpdateGoogleContact,
  isDirty,
  loading,
  disabled,
  hasGoogleId,
  sx,
}: GoogleButtonProps) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const isMobile = useIsMobile('md');

  const menuOptions = [
    <PopupMenuItem
      key="save-as-new"
      actionText="Save as new Google contact"
      onClick={() => {
        void handleSaveGoogleContact();
      }}
    />,
    <PopupMenuItem
      key="update-existing"
      actionText="Update existing Google contact"
      onClick={() => {
        void handleUpdateGoogleContact();
      }}
      disabled={!isDirty}
    />,
  ];

  if (!hasGoogleId) {
    return (
      <StyledButton
        onClick={() => {
          void handleSaveGoogleContact();
        }}
        loading={loading}
        disabled={disabled}
        isMobile={isMobile}
        sx={sx}
      />
    );
  }

  return isMobile ? (
    <>
      <Tooltip title={isDirty ? '' : 'No changes have been made'}>
        <span>
          <StyledButton
            onClick={() => setIsDrawerOpen(true)}
            loading={loading}
            disabled={disabled || !isDirty}
            dropdownIcon
            isMobile={isMobile}
            sx={sx}
          />
        </span>
      </Tooltip>
      <Drawer
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        anchor="bottom"
      >
        <MenuList>{menuOptions}</MenuList>
      </Drawer>
    </>
  ) : (
    <PopupMenu
      anchor={
        <Tooltip title={isDirty ? '' : 'No changes have been made'}>
          <span>
            <StyledButton
              loading={loading}
              disabled={disabled || !isDirty}
              dropdownIcon
              variant="outlined"
              color="utility"
              isMobile={isMobile}
              sx={sx}
            />
          </span>
        </Tooltip>
      }
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      sx={{ alignSelf: 'stretch' }}
    >
      {menuOptions}
    </PopupMenu>
  );
}
